<template>
  <div class="modal-content-pp soporte-modal">
    <ion-header class="headerTop">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="closeModal()">
            <ion-icon class="closebutton" src="assets/ICONOS/atras.svg"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>Soporte</ion-title>
      </ion-toolbar>
    </ion-header>

    <ion-content class="ion-padding sup-contentt">
      <div class="device-id-line" @click="copyDeviceId" :title="copied ? 'Copiado!' : 'Click para copiar ID'">
        <ion-icon src="assets/ICONOS/device.svg"></ion-icon>
        <span class="id-label">ID dispositivo:</span>
        <code>{{ deviceId }}</code>
        <span class="copy-badge" :class="{ copied }">{{ copied ? 'Copiado' : 'Copiar' }}</span>
      </div>

      <div class="desc">
        Diagnostico rapido del reproductor y conectividad. 
        <ion-icon
          @click="refreshDiagnostics" 
          src="assets/ICONOS/reload.svg" 
          style="cursor: pointer;"
          :disabled="refreshing"
          :style="refreshing ? 'animation: spin .2s linear infinite;' : ''"
          ></ion-icon>
      </div>

      <div class="block">
        <h4>Accesos necesarios</h4>
        <div class="item">El usuario debe tener acceso sin bloqueo de red a:</div>
        <button class="url-item" v-for="entry in accessChecks" :key="entry.fullUrl">
          <span class="status" :class="entry.ok === true ? 'ok' : (entry.ok === false ? 'fail' : 'pending')">
            {{ entry.ok === true ? 'OK' : (entry.ok === false ? 'ERROR' : '...') }}
          </span>
          <span>{{ entry.url }}</span>
        </button>
      </div>

      <div class="block">
        <h4>WakeLock info</h4>
        <div class="item"><strong>Activo:</strong> {{ wakeLockActiveLabel }}
           <span class="yellowbar">|</span> <strong>Modo:</strong>{{ wakeLockModeLabel }}
           <span class="yellowbar">|</span> <strong>Compatible:</strong>{{ wakeLockCompatibleLabel }}
           <span class="yellowbar">|</span> <strong>Version:</strong>{{ browserVersion }}</div>
        <div class="item"><strong>Dispositivo:</strong>{{ deviceLabel }}</div>
      </div>

      <div class="block" v-if="isMobilePlatform">
        <h4>Segundo plano</h4>
        <div class="item"><strong>Plataforma:</strong> {{ mobilePlatformLabel }}</div>
        <div class="item"><strong>Estado:</strong> {{ backgroundStatusLabel }}</div>
        <div class="item hint">Si aparece restringido, quitar optimizacion de bateria y permitir actividad en segundo plano para la app.</div>
      </div>

      <div class="block">
        <h4>Historial SpeedTest (ultimas 10)</h4>
        <div v-if="speedTestHistory.length === 0" class="item">Sin muestras registradas.</div>
        <div v-else class="speedtest-table-wrap">
          <table class="speedtest-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Hora</th>
                <th>Velocidad</th>
                <th>Estado</th>
                <th>Fallas</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(entry, index) in speedTestHistory" :key="entry.id">
                <td>{{ index + 1 }}</td>
                <td>{{ entry.timeLabel }}</td>
                <td><strong>{{ entry.mbpsLabel }}</strong></td>
                <td>
                  <span class="speed-badge" :class="entry.trafficClass">{{ entry.trafficLabel }}</span>
                </td>
                <td>{{ entry.failedLabel }}/{{ entry.successCountLabel }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="actions">
        <ion-button expand="block" @click="refreshDiagnostics" :disabled="refreshing">
          {{ refreshing ? 'Actualizando...' : 'Actualizar lista' }}
        </ion-button>
      </div>
    </ion-content>
  </div>
</template>

<script>
import { IonContent, IonHeader, IonTitle, IonToolbar, IonButton, IonButtons, IonIcon, modalController } from '@ionic/vue';
import { defineComponent } from 'vue';
import { Capacitor } from '@capacitor/core';

export default defineComponent({
  name: 'supportModal',
  components: { IonContent, IonHeader, IonTitle, IonToolbar, IonButton, IonButtons, IonIcon },
  props: {
    wakeLockStatus: { type: Object, default: () => ({}) },
    deviceId: { type: String, default: '' },
  },
  data() {
    return {
      refreshing: false,
      copied: false,
      speedTestHistory: [],
      accessChecks: [
        { url: 'vybroo.net', fullUrl: 'https://vybroo.net/favicon.ico', ok: null },
        { url: 'fonts.gstatic.com', fullUrl: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf', ok: null },
        { url: 'media.vybroo.com', fullUrl: 'https://media.vybroo.com/images/vybroo-test.jpg', ok: null },
      ],
    };
  },
  computed: {
    platform() {
      return Capacitor.getPlatform();
    },
    isMobilePlatform() {
      return this.platform === 'android' || this.platform === 'ios';
    },
    mobilePlatformLabel() {
      return this.platform === 'ios' ? 'iOS' : 'Android';
    },
    wakeLockActiveLabel() {
      const active = !!(this.wakeLockStatus && this.wakeLockStatus.active);
      return active ? 'Si' : 'No';
    },
    wakeLockModeLabel() {
      const mode = this.wakeLockStatus && this.wakeLockStatus.mode ? this.wakeLockStatus.mode : 'desconocido';
      return mode;
    },
    wakeLockCompatibleLabel() {
      const supportedFromRep = this.wakeLockStatus && typeof this.wakeLockStatus.supported === 'boolean'
        ? this.wakeLockStatus.supported
        : (typeof navigator !== 'undefined' && !!(navigator.wakeLock && navigator.wakeLock.request));
      return supportedFromRep ? 'Si' : 'No';
    },
    browserVersion() {
      const ua = navigator.userAgent || '';
      const m = ua.match(/(Edg|OPR|Chrome|Firefox|Safari)\/?\s*(\d+)/i);
      if (!m) return 'No disponible';
      const name = m[1] === 'OPR' ? 'Opera' : (m[1] === 'Edg' ? 'Edge' : m[1]);
      return name + ' ' + m[2];
    },
    deviceLabel() {
      const ua = navigator.userAgent || 'No disponible';
      return this.platform + ' | ' + ua;
    },
    backgroundStatusLabel() {
      if (!this.isMobilePlatform) return 'No aplica';
      const status = this.wakeLockStatus || {};
      if (status.nativeBackgroundConfigured) return 'Sin restriccion detectada por app';
      return 'Posible restriccion o no confirmado';
    },
  },
  methods: {
    closeModal() {
      modalController.dismiss({ dismissed: true });
    },
    async copyDeviceId() {
      try {
        await navigator.clipboard.writeText(this.deviceId);
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      } catch (e) {
        console.warn('Clipboard no disponible:', e);
      }
    },
    getSpeedTrafficLevel(mbpsValue) {
      // Semaforo: rojo < 3, amarillo 3-10, verde > 10
      if (!Number.isFinite(mbpsValue) || mbpsValue <= 0) {
        return { trafficClass: 'unknown', trafficLabel: 'N/D' };
      }
      if (mbpsValue < 2) {
        return { trafficClass: 'low', trafficLabel: 'Baja' };
      }
      if (mbpsValue <= 5) {
        return { trafficClass: 'medium', trafficLabel: 'Media' };
      }
      return { trafficClass: 'high', trafficLabel: 'Alta' };
    },
    loadSpeedTestHistory() {
      try {
        const raw = localStorage.getItem('speedTestHistory') || '[]';
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
          this.speedTestHistory = [];
          return;
        }

        this.speedTestHistory = parsed
          .slice(-10)
          .reverse()
          .map((item, idx) => {
            const mbpsRaw = item && item.mbps ? item.mbps.toString() : '';
            const mbpsNumeric = parseFloat(mbpsRaw.replace('Mbps', '').replace('MBPS', '').trim());
            const traffic = this.getSpeedTrafficLevel(mbpsNumeric);

            return {
              id: `speedtest-${idx}-${item.time || ''}-${item.mbps || ''}`,
              timeLabel: item && item.time ? item.time : 'Hora no disponible',
              mbpsLabel: item && item.mbps ? item.mbps : 'No disponible',
              successLabel: item && item.successfulSpeeds != null && item.totalSamples != null
                ? `${item.successfulSpeeds}/${item.totalSamples}`
                : 'No disponible',
              successCountLabel: item && item.successfulSpeeds != null ? item.successfulSpeeds : 'No disponible',
              failedLabel: item && item.failedSamples != null ? item.failedSamples : 'No disponible',
              trafficClass: traffic.trafficClass,
              trafficLabel: traffic.trafficLabel,
            };
          });
      } catch (error) {
        console.warn('No se pudo cargar speedTestHistory:', error);
        this.speedTestHistory = [];
      }
    },
    async checkUrlAccess(url) {
      let timeout = null;
      try {
        const controller = new AbortController();
        timeout = setTimeout(() => controller.abort(), 6000);
        await fetch(url, {
          method: 'GET',
          mode: 'no-cors',
          cache: 'no-store',
          signal: controller.signal,
        });
        clearTimeout(timeout);
        return true;
      } catch (error) {
        clearTimeout(timeout);
        return false;
      }
    },
    async refreshDiagnostics() {
      console.log('Iniciando refresh de diagnosticos...');
      this.refreshing = true;
      try {
        if (this.emitter) {
          this.emitter.emit('support-request-wakelock-status', 'support-modal-refresh');
        }
        this.loadSpeedTestHistory();
        const checks = await Promise.all(this.accessChecks.map(async (entry) => {
          const ok = await this.checkUrlAccess(entry.fullUrl);
          return { ...entry, ok };
        }));
        this.accessChecks = checks;
      } finally {
        this.refreshing = false;
      }
    },
  },
  mounted() {
    this.refreshDiagnostics();
  },
});
</script>

<style scoped>
.yellowbar {
  display: inline-block;
  margin: 0 2px;
  color: #888;
}
.closebutton {
  color: #fff;
  display: block;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.sup-contentt {
  margin: 0 auto !important;
    padding: 10px !important;
    color: #ccc;
    flex: 1;
    overflow-y: auto;
}
.modal-content-pp {
  height: 100vh;
  display: flex;
  flex-direction: column; 
}
.sup-contentt .block {
  margin: 14px;
  padding: 10px;
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 6px;
}
.sup-contentt .block h4 {
  margin: 0 0 8px 0;
  color: #eee;
}
.sup-contentt .item {
  margin: 4px 0;
  word-break: break-word;
}
.sup-contentt .hint {
  font-size: 10px;
  color: #444;
}
.sup-contentt .url-item {
  margin: 2px 5px;
  cursor: text;
  padding: 0px;
  background-color: transparent;
  border: none;
}
.sup-contentt .status {
  min-width: 50px;
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  border-radius: 10px;
  padding: 2px 6px;
}
.sup-contentt .status.ok {
  background: #dff6e4;
  color: #126b2d;
}
.sup-contentt .status.fail {
  background: #ffdede;
  color: #8d1818;
}
.sup-contentt .status.pending {
  background: #ececec;
  color: #555;
}
.sup-contentt .actions {
  margin: 14px;
}
.sup-contentt .desc {
  margin: 14px;
}
.sup-contentt .speedtest-table-wrap {
  overflow-x: auto;
}
.sup-contentt .speedtest-table {
  width: 100%;
  border-collapse: collapse; 
}
.sup-contentt .speedtest-table th,
.sup-contentt .speedtest-table td {
  border: 1px solid #e4e4e4;
  padding: 6px 8px;
  text-align: left;
  font-size: 10px;
}
.sup-contentt .speedtest-table th {
  background: #2c2c2c;
  font-weight: 700;
}
.sup-contentt .speed-badge {
  border-radius: 12px;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
}
.sup-contentt .speed-badge.low {
  background: #ffdede;
  color: #8d1818;
}
.sup-contentt .speed-badge.medium {
  background: #fff3cf;
  color: #8a6200;
}
.sup-contentt .speed-badge.high {
  background: #dff6e4;
  color: #126b2d;
}
.sup-contentt .speed-badge.unknown {
  background: #ececec;
  color: #555;
}
.soporte-modal  {
  font-size: 11px;
}
.device-id-line {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  font-size: 12px;
  color: #fff;
  cursor: pointer;
  user-select: all;
  border-bottom: 1px solid rgba(255,255,255,0.15);
  background: rgba(255,255,255,0.04);
}
.device-id-line:hover {
  background: rgba(255,255,255,0.1);
}
.id-label {
  color: #94a3b8;
  font-size: 11px;
  white-space: nowrap;
}
.device-id-line code {
  font-family: monospace;
  font-size: 11px;
  color: #e2e8f0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.device-id-line ion-icon {
  font-size: 15px;
  color: #94a3b8;
}
.copy-badge {
  font-size: 10px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 4px;
  border: 1px solid rgba(255,255,255,0.15);
  transition: all 0.15s;
}
.copy-badge.copied {
  background: #166534;
  color: #86efac;
  border-color: #22c55e;
}
@media only screen and (min-width: 768px) and (min-height: 768px) {
    .sc-ion-modal-md-h {
        --width: 700px !important;
        --height: 700px !important;
        width: 700px !important;
        height: 700px !important;
    }
}
</style>